import React, { useState } from 'react';
import { 
  X, 
  Edit, 
  Trash2, 
  Calendar,
  MapPin,
  User,
  Phone,
  Mail,
  DollarSign,
  Users as UsersIcon,
  Package,
  CheckCircle,
  Clock,
  Truck,
  FileText
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

const EventDetails = ({ event, onClose, onEdit, onDelete, apiKey }) => {
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const handleQuickStatusChange = async (newStatus) => {
    setIsUpdatingStatus(true);
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/events/${event.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        // Refresh event data
        window.location.reload();
      }
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'delivered': return 'bg-green-100 text-green-800 border-green-200';
      case 'completed': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'in_progress': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'confirmed': return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch(status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'partial': return 'bg-yellow-100 text-yellow-800';
      case 'pending': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 overflow-y-auto">
      <Card className="w-full max-w-4xl my-8">
        <CardContent className="p-0">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white p-6 rounded-t-2xl">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold mb-2">{event.client_name}</h2>
                <div className="flex gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium border-2 ${getStatusColor(event.status)}`}>
                    {event.status === 'pending' ? 'En attente' :
                     event.status === 'confirmed' ? 'Confirmé' :
                     event.status === 'in_progress' ? 'En cours' :
                     event.status === 'completed' ? 'Terminé' :
                     event.status === 'delivered' ? 'Livré' : event.status}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPaymentStatusColor(event.payment_status)}`}>
                    {event.payment_status === 'paid' ? '✅ Payé' :
                     event.payment_status === 'partial' ? '⚠️ Acompte' : '❌ Non payé'}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => onEdit(event)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <Edit className="h-5 w-5" />
                </button>
                <button 
                  onClick={onClose}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Quick Actions */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Actions Rapides</h3>
              <div className="flex flex-wrap gap-2">
                {event.status !== 'confirmed' && (
                  <Button
                    size="sm"
                    onClick={() => handleQuickStatusChange('confirmed')}
                    disabled={isUpdatingStatus}
                    className="bg-cyan-500 hover:bg-cyan-600"
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Confirmer
                  </Button>
                )}
                {event.status !== 'in_progress' && event.status !== 'completed' && event.status !== 'delivered' && (
                  <Button
                    size="sm"
                    onClick={() => handleQuickStatusChange('in_progress')}
                    disabled={isUpdatingStatus}
                    className="bg-purple-500 hover:bg-purple-600"
                  >
                    <Clock className="h-4 w-4 mr-1" />
                    Démarrer
                  </Button>
                )}
                {event.status !== 'completed' && event.status !== 'delivered' && (
                  <Button
                    size="sm"
                    onClick={() => handleQuickStatusChange('completed')}
                    disabled={isUpdatingStatus}
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Terminer
                  </Button>
                )}
                {event.status !== 'delivered' && (
                  <Button
                    size="sm"
                    onClick={() => handleQuickStatusChange('delivered')}
                    disabled={isUpdatingStatus}
                    className="bg-green-500 hover:bg-green-600"
                  >
                    <Truck className="h-4 w-4 mr-1" />
                    Livrer
                  </Button>
                )}
              </div>
            </div>

            {/* Client Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="h-5 w-5 text-amber-600" />
                Informations Client
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <User className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Nom</p>
                    <p className="font-medium">{event.client_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Téléphone</p>
                    <a href={`tel:${event.client_phone}`} className="font-medium text-blue-600 hover:underline">
                      {event.client_phone}
                    </a>
                  </div>
                </div>
                {event.client_email && (
                  <div className="flex items-center gap-3 md:col-span-2">
                    <Mail className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <a href={`mailto:${event.client_email}`} className="font-medium text-blue-600 hover:underline">
                        {event.client_email}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Event Details */}
            <div className="pt-4 border-t">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-amber-600" />
                Détails de l'Événement
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Type</p>
                  <p className="font-medium">{event.event_type}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="font-medium">{formatDate(event.event_date)}</p>
                </div>
                {event.event_time && (
                  <div>
                    <p className="text-sm text-gray-500">Heure</p>
                    <p className="font-medium">{event.event_time}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-500">Ville</p>
                  <p className="font-medium">{event.event_city}</p>
                </div>
                <div className="md:col-span-2 flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm text-gray-500">Lieu</p>
                    <p className="font-medium">{event.event_location}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="pt-4 border-t">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-amber-600" />
                Tarification
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Package</p>
                  <p className="font-medium flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    {event.package_type}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Montant Total</p>
                  <p className="font-medium text-lg text-green-600">${event.total_amount.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Acompte Payé</p>
                  <p className="font-medium">${event.deposit_paid.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Solde Restant</p>
                  <p className={`font-medium text-lg ${event.balance_due > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    ${event.balance_due.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            {/* Team */}
            {(event.photographer || event.videographer) && (
              <div className="pt-4 border-t">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <UsersIcon className="h-5 w-5 text-amber-600" />
                  Équipe Assignée
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {event.photographer && (
                    <div>
                      <p className="text-sm text-gray-500">Photographe</p>
                      <p className="font-medium">{event.photographer}</p>
                    </div>
                  )}
                  {event.videographer && (
                    <div>
                      <p className="text-sm text-gray-500">Vidéaste</p>
                      <p className="font-medium">{event.videographer}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Delivery Status */}
            <div className="pt-4 border-t">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Truck className="h-5 w-5 text-amber-600" />
                État de Livraison
              </h3>
              <div>
                <p className="font-medium">
                  {event.delivery_status === 'not_started' ? 'Non commencé' :
                   event.delivery_status === 'editing' ? 'En montage' :
                   event.delivery_status === 'ready' ? 'Prêt' :
                   event.delivery_status === 'delivered' ? 'Livré' : event.delivery_status}
                </p>
              </div>
            </div>

            {/* Notes */}
            {(event.notes || event.internal_notes) && (
              <div className="pt-4 border-t">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-amber-600" />
                  Notes
                </h3>
                {event.notes && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-500 mb-1">Notes Client</p>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{event.notes}</p>
                  </div>
                )}
                {event.internal_notes && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Notes Internes</p>
                    <p className="text-gray-700 bg-yellow-50 p-3 rounded-lg border border-yellow-200">{event.internal_notes}</p>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                onClick={() => onEdit(event)}
                className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
              >
                <Edit className="h-4 w-4 mr-2" />
                Modifier
              </Button>
              <Button
                onClick={() => {
                  if (window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) {
                    onDelete(event.id);
                    onClose();
                  }
                }}
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Supprimer
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EventDetails;
